<template>
    <div class="course__edit_enrolment">
        <component-title
            :size="'small'"
            :underline="true"
        >Enrolment type</component-title>
        <form-radio-horizontal
            :label="''"
            :name="'course-edit-enrolment-type'"
            :items="[
                {
                    name: 'Open',
                    value: 'open'
                },
                {
                    name: 'Conditional',
                    value: 'conditional'
                }
            ]"
            :value="courseRequireApproval ? 'conditional' : 'open'"
            @input="setWorkspaceSettingsRequireApproval"
        >An open enrolment allow learners to enrol onto the course without any input from administrators. <br>A conditional enrolment needs acceptance from an administrator when a learner enrols onto the course.
        </form-radio-horizontal>
        <component-title
            :size="'small'"
            :underline="true"
        >Course visibility</component-title>
        <component-text
            :size="'small'"
        >You can choose which learners can see a course by making it visible to a whole workspace or specific Organisation Units.

            Learners can still be enrolled on hidden courses manually or by using auto-enrolments and workflows.
        </component-text>
        <template v-if="requestPending">
            <component-loading></component-loading>
        </template>
        <template v-else>
            <component-wrapper>
                <div class="course__edit_enrolment_visibility">
                    <component-title
                        :size="'medium'"
                    >This workspace</component-title>
                    <form-radio-horizontal
                        :class="'course__edit_enrolment_visibility_orgunits'"
                        :name="'course-edit-enrolment-visibility-orgunits'"
                        :label="'Organisation unit'"
                        :value="courseWorkspaceSettingsVisibilityOutput"
                        :items="[
                            {
                                name: 'All',
                                value: 'all'
                            },
                            {
                                name: 'Specific',
                                value: 'specific'
                            },
                            {
                                name: 'Hidden',
                                value: 'hidden'
                            }
                        ]"
                        @input="setCourseWorkspaceSettingsVisibility"
                    >
                        <template v-if="courseWorkspaceSettingsVisibilityOutput === 'all'">
                            This course is visible to all learners in this Workspace.
                        </template>
                        <template v-else-if="courseWorkspaceSettingsVisibilityOutput === 'specific'">
                            This course is visible to learners in the selected Organisation Units in this Workspace.
                        </template>
                        <template v-else-if="courseWorkspaceSettingsVisibilityOutput === 'hidden'">
                            This course is hidden from all learners in this workspace.
                        </template>
                    </form-radio-horizontal>
                    <form-select-org-units
                        :keep-alive="false"
                        :label="''"
                        :placeholder="courseWorkspaceSettingsVisibilityOutput === 'all' ? 'All units' : (courseWorkspaceSettingsVisibilityOutput === 'hidden' ? 'No organisation units' : 'Select organisation units')"
                        :disabled="courseWorkspaceSettingsVisibilityOutput !== 'specific'"
                        :name="'course-edit-enrolment-orgunits'"
                        :value="courseWorkspaceSettingsOrgUnits"
                        :multiple="true"
                        @input="setCourseWorkspaceSettingsOrgUnits"
                    ></form-select-org-units>
                    <component-title
                        :size="'medium'"
                    >Other workspace visibility</component-title>
                    <div class="course__edit_enrolment_visibility_otherworkspaces">
                        <template v-for="(workspace, index) in courseWorkspaceSettingsWorkspacesWithLEARole">
                            <div class="columns is-vcentered">
                                <div class="column">
                                    <component-text :heading="true">{{ workspace.name }}</component-text>
                                </div>
                                <div class="column is-narrow">
                                    <component-status :status="workspace.settings.visible === false ? 'hidden' : (workspace.settings.availableInOrganisationUnits ? 'specific learners' : 'all learners')"></component-status>
                                </div>
                                <div class="column is-narrow">
                                    <component-button
                                        :name="`course-edit-enrolment-visibility-workspace-${workspace.id}`"
                                        :text="true"
                                        :icon="true"
                                        :icon-align="'right'"
                                        :icon-name="'arrow-right-circle'"
                                        @click="openNotifySwitchWorkspace({ name: workspace.name, id: workspace.id })"
                                    >View visibility settings</component-button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </component-wrapper>
        </template>
        <pop-notify
            :class="'pop__notify--switchworkspace'"
            ref="notifyswitchworkspace"
            :title-text="`Switch to ${switchToWorkspace.name}?`"
            :content-text="`Any changes you have made may not be saved.`"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Switch'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            @emit-submit="switchWorkspace(switchToWorkspace.id)"
        />
    </div>
</template>

<script>

import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import ComponentOrgUnitsFilterItem from '@/components/default/shared/ComponentOrgUnitsFilterItem'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentSeparator from '@/components/default/shared/ComponentSeparator'
import ComponentText from '@/components/default/shared/ComponentText'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import ComponentStatus from '@/components/default/shared/ComponentStatus'
import ComponentButton from '@/components/default/shared/ComponentButton'
import PopNotify from '@/components/default/shared/PopNotify'

export default {
  name: 'course-edit-enrolment',
  components: { PopNotify, ComponentButton, ComponentStatus, FormSelectOrgUnits, ComponentText, ComponentSeparator, ComponentTitle, FormCheckbox, ComponentLoading, ComponentWrapper, ComponentOrgUnitsFilterItem, FormRadioHorizontal },
  data () {
    return {
      requestPending: false,
      switchToWorkspace: {
        name: '',
        id: ''
      }
    }
  },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  computed: {
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    loggedInUserCustomerId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    courseWorkspaceSettingsVisibility () {
      return this.$store.getters.getEbsCatalogItemWorkspaceSettingsVisibility(this.loggedInUserWorkspaceId)
    },
    courseWorkspaceSettingsOrgUnits () {
      return this.$store.getters.getEbsCatalogItemWorkspaceSettingsOrgUnits(this.loggedInUserWorkspaceId)
    },
    courseWorkspaceSettingsVisibilityOutput () {
      const visibility = this.courseWorkspaceSettingsVisibility
      const orgUnits = this.courseWorkspaceSettingsOrgUnits
      let output

      if (visibility === false) {
        output = 'hidden'
      } else if (visibility === true && orgUnits) {
        output = 'specific'
      } else if (visibility === true && !orgUnits) {
        output = 'all'
      }
      return output
    },
    courseRequireApproval () {
      return this.$store.getters.getEbsCatalogItemWorkspaceSettingsRequireApproval
    },
    courseWorkspaceSettingsWorkspaces () {
      return this.$store.getters.getEbsCatalogItemWorkspacesSettings
    },
    courseWorkspaceSettingsWorkspacesWithLEARole () {
      const workspacesSettings = this.courseWorkspaceSettingsWorkspaces
      const userProfilesWithLEARole = this.userProfilesWithLEARole
      const loggedInUserWorkspaceId = this.loggedInUserWorkspaceId

      return workspacesSettings && workspacesSettings.length ? workspacesSettings.filter(obj => userProfilesWithLEARole.includes(obj.settings.workspaceId) && obj.settings.workspaceId !== loggedInUserWorkspaceId) : []
    },
    userProfilesWithLEARole () {
      return this.$store.getters.getLmsSuperadminUserProfilesWithLEARoleInCustomerWorkspaceId(this.loggedInUserCustomerId)
    },
    rootItemName () {
      return 'course'
    },
    //
    isAddMode () {
      return this.$route.name.includes('course-add')
    },
    isEditMode () {
      return this.$route.name.includes('course-edit')
    },
    isShowMode () {
      return this.$route.name.includes('course-show')
    },
    isRouteCatalogItem () {
      return this.$route.name.includes('ebs-catalog-item-edit')
    }
  },
  methods: {
    setCourseWorkspaceSettingsOrgUnits (data) {
      let dataMapped = data && data.length ? data.map(obj => obj.id) : []
      this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsOrgUnits', { workspaceId: this.loggedInUserWorkspaceId, value: dataMapped })
    },
    setCourseWorkspaceSettingsVisibility (data) {
      if (data === 'all') {
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsVisibility', { workspaceId: this.loggedInUserWorkspaceId, value: true })
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsOrgUnits', { workspaceId: this.loggedInUserWorkspaceId, value: null })
      } else if (data === 'specific') {
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsVisibility', { workspaceId: this.loggedInUserWorkspaceId, value: true })
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsOrgUnits', { workspaceId: this.loggedInUserWorkspaceId, value: [] })
      } else if (data === 'hidden') {
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsVisibility', { workspaceId: this.loggedInUserWorkspaceId, value: false })
        this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsOrgUnits', { workspaceId: this.loggedInUserWorkspaceId, value: null })
      }
    },
    setWorkspaceSettingsRequireApproval (data) {
      this.$store.dispatch('ebsCatalogItemSetWorkspaceSettingsRequireApproval', data === 'conditional')
    },
    getCourseWorkspaceSettings () {
      this.requestPending = true
      const catalogId = this.$route.params.catalogId
      this.$store.dispatch('ebsCatalogItemGetWorkspaceSettings', catalogId).finally(() => {
        this.requestPending = false
        this.$eventBus.$emit('stop-loading')
      })
    },
    openNotifySwitchWorkspace (data) {
      this.switchToWorkspace = data
      this.$refs.notifyswitchworkspace.openNotify()
    },
    closeNotifySwitchWorkspace (data) {
      this.$refs.notifyswitchworkspace.closeNotify()
    },
    switchWorkspace (data) {
      this.requestPending = true
      this.$store.dispatch('authUserLoginSSOWorkspace', data).then(() => {
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Workspace accessed',
          status: 'success'
        })
        this.getCourseWorkspaceSettings()
      }).catch(() => {
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Workspace not accessed',
          message: 'There is a problem accessing this workspace. Please check you have access to this workspace.',
          status: 'error'
        })
        this.requestPending = false
      }).finally(() => {
        this.closeNotifySwitchWorkspace()
      })
    }
  },
  created () {
    if (!this.courseWorkspaceSettingsWorkspaces.length && this.isRouteCatalogItem) {
      this.getCourseWorkspaceSettings()
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courseaddeditenrolment";
</style>
